/**
 * Pages slice (SCRUM-722) — createSlice/createAsyncThunk migration of
 * pageReducer + pageAction, fast-follow on the sites pilot (SCRUM-596,
 * sitesSlice.ts). State is typed from the shared server/client contracts
 * (shared/apiTypes.js), so useSelector call-sites reading this slice are
 * field-name checked. The legacy import path `./pageAction` re-binds
 * everything exported here; new code should import from this module.
 *
 * Pilot patterns reused (see sitesSlice.ts for the rationale):
 * - Cross-slice error coupling: every thunk catch keeps dispatching the plain
 *   GET_ERRORS action with the exact payload the legacy pageAction sent —
 *   flows that sent none (addPage, addRequiredPage, updatePageContent) still
 *   send none. rejectWithValue carries the *original* error so the unwrap()
 *   wrappers reproduce the legacy resolve/reject contracts (callers read
 *   err.response.data off the rejection).
 * - Cross-slice state coupling: GET_PAGE_BY_ID stays a plain legacy string.
 *   translationservice declares the same string value: transpagebyid
 *   dispatches it (landing in this slice via extraReducers), and
 *   translationReducer holds a case for it (fed by getPageById here), so both
 *   directions only survive if the string survives.
 * - Legacy call signatures/contracts that don't fit the single-arg thunk API
 *   are kept as thin unwrap() wrappers (addPage, addRequiredPage,
 *   updatePageContent) or as hand-rolled thunks where
 *   the exact flow is pinned by contract tests (deletePage, getPageById).
 *
 * Dropped as dead during this migration (no importer / listener / reader
 * anywhere, verified during SCRUM-722 — same style as SCRUM-596's dropped
 * exports): updatePage + UPDATE_PAGE, updateRequiredPage +
 * UPDATE_REQUIRED_PAGE, getSubCategoriesbyId + SUBCATEGORY_BY_ID + the
 * sub_category_product state key (the app uses categoryAction's
 * getSubCategoriesbyIds instead), the inert UPDATE_PAGE_CONTENT dispatch (no
 * reducer case anywhere), and the no-op ADD_PENDING_PAGE case (it listened
 * for "ADD_PENDING_PAGE", which nothing dispatches — pendingPagesActions
 * emits "ADD_PENDING_PAGES").
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { Action, PayloadAction } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";
import type {
  PagesBySiteResponse,
  PageWriteResponse,
  PageCreateResponse,
  PageByIdResponse,
  PageListResponse,
} from "../../../../shared/apiTypes";
import api from "../api";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { GET_PAGE_BY_ID } from "./pageTypes";
import {
  DeletePageFromSite,
  getSitesByDomain,
} from "../siteServices/sitesSlice";
import type { PayloadCreatorDispatch } from "../siteServices/sitesSlice";
import { reportSoftFailure } from "../../layouts/Alerts";
import { asCaught, errorPayload } from "../caughtError";
import type { AppDispatch } from "../store";

/**
 * The object a category / sub_category selection stores. The array the
 * filtering readers dereference is REQUIRED on the type, so a payload without
 * it no longer typechecks where a pick is built (SCRUM-1650); the other
 * fields are whatever the list routes deliver on a click, or what getPageById
 * projects for a stored target (`slug`, `category.slug`; SCRUM-1651).
 */
export type CategorySelection = { _id?: any; subcategories: any[]; [field: string]: any }; // any: whatever the list routes deliver on a click
export type SubcategorySelection = { _id?: any; products: any[]; [field: string]: any }; // any: whatever the list routes deliver on a click

/**
 * A page-type pick: {} (nothing picked) or the selection,
 * discriminated on `type`. `object` is polymorphic per `type` — a path string
 * for simple_page/error/maintenance/redirect picks, an entity object otherwise
 * (product / post / author readers dereference their own fields, hence `any`
 * there); for category/sub_category it carries the array the filtering readers
 * dereference, which the union makes visible to tsc and pageTypePayloadDefect()
 * below guards before a screen stores it (SCRUM-855, SCRUM-1650). The
 * screens own the pick since SCRUM-2206 / SCRUM-2216; the slice keeps the type
 * because the create wizard's page-type step imports it.
 */
export type PageTypeSelection =
  | { type: "category"; object: CategorySelection }
  | { type: "sub_category"; object: SubcategorySelection }
  | { type: "product"; object: any } // any: the product reader dereferences its own fields
  | { type: "post"; object: any } // any: the post reader dereferences its own fields
  | { type: "author"; object: any } // any: the author reader dereferences its own fields
  | { type: "simple_page" | "error" | "maintenance" | "redirect"; object: string }
  | { type?: undefined; object?: undefined };

/**
 * One entry of the `referencingPages` array a refused page delete answers with
 * (server/controllers/page.js deletePage, SCRUM-729 + SCRUM-1259). The `_id` is
 * the field the message string cannot carry, and the only one that makes a
 * referrer reachable from the overview.
 */
export type PageDeleteBlocker = {
  _id: any; // any: an ObjectId on the server type, a string after JSON transport
  global_title?: string;
  page_configuration?: { path?: string };
  // SCRUM-2354: set only on a staged edit naming the page as its reference
  // page; its `_id` is a staged-edit id, not a page id.
  kind?: "stagedEdit";
};

export type PagesState = {
  pages: PagesBySiteResponse;
  page: PageWriteResponse | never[];
  page_to_update: PageByIdResponse | Record<string, never>;
  required_page: PageWriteResponse | Record<string, never>;
  deleted: boolean;
  page_delete_blockers: PageDeleteBlocker[];
  filters: any[]; // any: the filter chips the Custom Pages table keeps; no row type exists yet
  list: PageListResponse;
  listParams: Record<string, string | number> | null;
};

const initialState: PagesState = {
  pages: [],
  // Legacy quirk kept: starts as [] but holds the last written page object.
  page: [],
  page_to_update: {},
  required_page: {},
  deleted: false,
  // SCRUM-1296: the pages a refused delete named as still linking to the
  // target. Lives in the store rather than in a screen's local state so it
  // survives the walk it exists for — opening a referrer unmounts the overview.
  page_delete_blockers: [],
  filters: [],
  // SCRUM-1811: the one page of the Custom Pages table the server answered, and
  // the params that asked for it, so a write can refetch exactly that page.
  list: { data: [], total: 0, page: 1, limit: 10 },
  listParams: null,
};

// category / sub_category page-type picks drive filtering logic
// (narrowToCategoryPage in UpdatePage/useDropdownOptions.ts, the create
// wizard's product pool) that reads object.subcategories / object.products as
// arrays. The pick is screen state since SCRUM-2206 (create wizard,
// PageFormContainer) and SCRUM-2216 (update screen, UpdatePageContainer); until
// then it was a field of this slice, written through a store action
// whose prepare held this guard. Both screens' handlers now call
// refusePageTypePayload before they store a pick: a payload without its array
// is REFUSED, the previous pick stays, and the refusal is reported with a code
// (SCRUM-1650; until then the guard coerced it to {}, which wiped a page's
// stored configuration with no signal, SCRUM-1637). getPageById answers a
// stored target with the arrays (SCRUM-1651), so the guard catches only a
// payload that breaks that contract.
export const PAGE_TYPE_PAYLOAD_INVALID = "PAGE_TYPE_PAYLOAD_INVALID";

/**
 * The array a category / sub_category payload must carry and does not, or
 * null when the payload is storable. Every other type (and the {} reset) is
 * storable as-is.
 * payload is unknown: the guard exists to refuse payloads that break the PageTypeSelection contract
 */
export const pageTypePayloadDefect = (payload: unknown): "subcategories" | "products" | null => {
  const p = payload as { type?: string; object?: { subcategories?: unknown; products?: unknown } } | null | undefined; // untrusted shape, read defensively below
  if (p?.type === "category" && !Array.isArray(p.object?.subcategories)) {
    return "subcategories";
  }
  if (p?.type === "sub_category" && !Array.isArray(p.object?.products)) {
    return "products";
  }
  return null;
};

/**
 * The refusal itself: reports a defective payload as a soft failure with the
 * PAGE_TYPE_PAYLOAD_INVALID code and returns true, or returns false when the
 * payload is storable. Called by the two screens that keep a page-type pick in
 * local state: the create wizard's onConfigurationChange (SCRUM-2206) and
 * UpdatePageContainer's onPageTypeChange (SCRUM-2216).
 * payload is unknown, as in pageTypePayloadDefect.
 */
export const refusePageTypePayload = (payload: unknown): boolean => {
  const missing = pageTypePayloadDefect(payload);
  if (!missing) return false;
  reportSoftFailure({
    code: PAGE_TYPE_PAYLOAD_INVALID,
    message:
      `The ${(payload as { type?: string }).type} selection was not stored: its object carries no ` +
      `${missing} array. The previous selection is kept.`,
  });
  return true;
};


// ---------------------------------------------------------------------------
// Async thunks. Each catch mirrors its legacy pageAction block; write order
// inside the payload creators matches the legacy dispatch order.
// ---------------------------------------------------------------------------

/** The page form's payload as the editors assemble it; no shared request type exists yet. */
type PageFormData = Record<string, any>; // any: free-form page content keyed by variable name

/**
 * What PageFormContainer reads off the resolved addPage(): the page, plus the
 * tag-page summary it toasts. Measured 2026-10-10 (SCRUM-2232): nothing under
 * P2CMS/server sends `tagPagesInfo`, so that branch of the container is dead
 * today; the type records what the container expects, not what the server does.
 */
export type AddPageResult = PageCreateResponse & {
  tagPagesInfo?: {
    created: number;
    skipped: number;
    skippedDetails: { tagName: string }[];
  };
};

// Get pages of the current site — the state write rides .fulfilled.
export const getPagesByCurrentSite = createAsyncThunk(
  "pages/getPagesByCurrentSite",
  async (_arg: void, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<PagesBySiteResponse> = await api.get("/api/pages/pagesBySite");
      return res.data;
    } catch (err) {
      // SCRUM-856: surface the real error via the hub instead of blanking the
      // pages list on a fetch failure; the previously loaded pages stay in place.
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

/**
 * SCRUM-1811: one page of the Custom Pages table, paged, filtered and searched
 * on the server (GET /api/pages, docs/Admin_List_Query.md) instead of the site
 * document's pages[], which getCurrentSite fetches 50 at a time.
 */
export const fetchPageList = createAsyncThunk(
  "pages/fetchPageList",
  async (params: Record<string, string | number>, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<PageListResponse> = await api.get("/api/pages", { params });
      return res.data;
    } catch (err) {
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

/**
 * SCRUM-1811: after a page is created, deleted or published, fetch the table's
 * current page again rather than splice a local copy. A no-op until the table
 * has asked for a page once.
 */
export const refetchPageList = () => (
  dispatch: PayloadCreatorDispatch,
  getState: () => unknown
) => {
  const params = (getState() as { pages?: PagesState } | undefined)?.pages?.listParams; // the store root as far as this slice reads it
  if (params) dispatch(fetchPageList(params));
};

const addPageThunk = createAsyncThunk(
  "pages/addPage",
  async (data: PageFormData, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<AddPageResult> = await api.post("/api/pages/add", data);
      dispatch(pagesSlice.actions.pageAdded(res.data));
      dispatch(refetchPageList());
      // Return response data so the component can access tagPagesInfo.
      return res.data;
    } catch (err) {
      console.error("Error adding page:", err);
      return rejectWithValue(err);
    }
  }
);

/**
 * Add a page. Legacy contract: resolves with res.data (PageFormContainer
 * reads tagPagesInfo off it) and rejects with the original error, hence the
 * unwrap() wrapper.
 */
export const addPage = (data: PageFormData) => (dispatch: AppDispatch) =>
  dispatch(addPageThunk(data)).unwrap();

const addRequiredPageThunk = createAsyncThunk(
  "pages/addRequiredPage",
  async (data: PageFormData, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<PageWriteResponse> = await api.post("/api/pages/addNavBarFooterPage", data);
      dispatch(pagesSlice.actions.requiredPageAdded(res.data));
      dispatch(getSitesByDomain());
    } catch (err) {
      // Legacy had no catch: the rejection propagates to the caller
      // (RequiredPagesContainer awaits in try/finally) and no GET_ERRORS is sent.
      return rejectWithValue(err);
    }
  }
);

/**
 * Add a navigation/footer page. Legacy contract: the returned promise rejects
 * with the original error, hence the unwrap() wrapper.
 */
export const addRequiredPage = (data: PageFormData) => (dispatch: AppDispatch) =>
  dispatch(addRequiredPageThunk(data)).unwrap();

const updatePageContentThunk = createAsyncThunk(
  "pages/updatePageContent",
  async (data: PageFormData, { dispatch, rejectWithValue }) => {
    try {
      await api.put<PageWriteResponse>("/api/pages/update/pagecontent", data);
      dispatch(getSitesByDomain());
      // The caller (UpdatePageHeaderFooterContainer) awaits this thunk and toasts
      // success / failure in its own screen context, so there is no global sentinel
      // to round-trip; a rejected PUT propagates so the caller won't confirm on
      // failure, and the global notification hub surfaces genuinely unhandled
      // errors (SCRUM-883).
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Update header/footer page content. Legacy contract: the returned promise
 * rejects with the original error, hence the unwrap() wrapper.
 */
export const updatePageContent = (data: PageFormData) => (dispatch: AppDispatch) =>
  dispatch(updatePageContentThunk(data)).unwrap();

/**
 * Delete a page. Kept hand-rolled: the (id, callback) signature and the exact
 * resolve/reject + GET_ERRORS flow are pinned by
 * contentWriteActions.errorHandling.test.js (SCRUM-815).
 */
export const deletePage = (id: string, callback?: () => void) => async (dispatch: AppDispatch) => {
  try {
    await api.delete<unknown>(`/api/pages/delete/${id}`);
    dispatch(pagesSlice.actions.pageDeleted());
    // SCRUM-1296: the delete the editor was blocked on has now gone through, so
    // the referrer list it produced is stale — drop it.
    dispatch(pagesSlice.actions.pageDeleteBlockersCleared());
    dispatch(DeletePageFromSite(id));
    dispatch(refetchPageList());
    dispatch({ type: GET_ERRORS, payload: {} });
    if (callback) {
      callback();
    }
    return id;
  } catch (err) {
    const data = asCaught(err)?.response?.data;
    // SCRUM-1296: keep the structured referrer list before the body is flattened
    // into a single string by the notification hub. GET_ERRORS below is
    // unchanged (its exact payload is pinned by SCRUM-815).
    if (Array.isArray(data?.referencingPages)) {
      dispatch(pagesSlice.actions.pageDeleteBlocked(data.referencingPages));
    }
    dispatch({
      type: GET_ERRORS,
      payload: data ?? { message: asCaught(err)?.message },
    });
    throw err;
  }
};

// Get page by id — kept hand-rolled: it dispatches the plain legacy
// GET_PAGE_BY_ID string on purpose (translationReducer holds a case for the
// same string; a createAsyncThunk type would sever that coupling).
export const getPageById = (id: string) => async (dispatch: AppDispatch) => {
  await api
    .get<PageByIdResponse>(`/api/pages/pageById/${id}`)
    .then((res) => {
      dispatch({
        type: GET_PAGE_BY_ID,
        payload: res.data,
      });
    })
    .catch((err) =>
      // Legacy parity: getPageById sends the raw error to the hub, not response.data.
      dispatch({
        type: GET_ERRORS,
        payload: err,
      })
    );
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const pagesSlice = createSlice({
  name: "pages",
  initialState,
  // The payload-carrying reducers use the prepare form, so the generated action
  // creator takes the payload type below and a wrongly-typed dispatch
  // (selectedFilters(42)) does not typecheck.
  reducers: {
    selectedFilters: {
      reducer(state, action: PayloadAction<any[]>) { // any: same filter chips as PagesState.filters
        state.filters = action.payload;
      },
      // Legacy creator spread the caller's array into a copy before dispatching.
      prepare(data: any[]) { // any: same filter chips as PagesState.filters
        return { payload: [...data] };
      },
    },
    clearPage(state) {
      state.page_to_update = {};
    },
    // Dispatched by addPageThunk after the POST resolves (the seeding point
    // reducer tests use as well).
    pageAdded: {
      reducer(state, action: PayloadAction<PageCreateResponse>) {
        state.page = action.payload;
        state.pages = [action.payload, ...state.pages];
      },
      prepare(payload: PageCreateResponse) {
        return { payload };
      },
    },
    // Dispatched by addRequiredPageThunk after the POST resolves.
    requiredPageAdded: {
      reducer(state, action: PayloadAction<PageWriteResponse>) {
        state.required_page = action.payload ?? {};
      },
      prepare(payload: PageWriteResponse) {
        return { payload };
      },
    },
    // Dispatched by deletePage after the DELETE resolves. Never reset — the
    // legacy flag behaved the same way.
    pageDeleted(state) {
      state.deleted = true;
    },
    // SCRUM-1296: dispatched by deletePage when the DELETE is refused with a
    // 409 that names the referrers. The array is stored verbatim — the rows the
    // editor walks are built from it, never parsed back out of errors[0].msg,
    // so rewording that sentence cannot break the list.
    pageDeleteBlocked: {
      reducer(state, action: PayloadAction<PageDeleteBlocker[]>) {
        state.page_delete_blockers = action.payload;
      },
      prepare(payload: PageDeleteBlocker[]) {
        return { payload };
      },
    },
    // Cleared on an explicit dismissal and on a delete that finally succeeds —
    // never on a timer, and never by a re-render (SCRUM-1296).
    pageDeleteBlockersCleared(state) {
      state.page_delete_blockers = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPagesByCurrentSite.fulfilled, (state, action) => {
        state.pages = action.payload;
      })
      .addCase(fetchPageList.pending, (state, action) => {
        state.listParams = action.meta.arg;
      })
      .addCase(fetchPageList.fulfilled, (state, action) => {
        state.list = action.payload;
      })
      // SCRUM-1863: once a save of the page held in `page_to_update` succeeds,
      // that copy is stale. The nav/footer editor reads it first and skips its
      // fetch while the id matches, so reopening the page showed the pre-save
      // values and a second save wrote them back. Dropping the copy makes the
      // next open fetch it again; an unsaved page keeps its fetch-once cache.
      .addCase(updatePageContentThunk.fulfilled, (state, action) => {
        const held: { _id?: unknown } | null = state.page_to_update;
        const savedId = action.meta.arg?._id;
        if (savedId && String(held?._id || "") === String(savedId)) {
          state.page_to_update = {};
        }
      })
      // Cross-slice: the plain legacy string shared with translationservice
      // (see the module header) — transpagebyid dispatches it into this slice.
      .addCase(GET_PAGE_BY_ID, (state, action: Action<typeof GET_PAGE_BY_ID> & { payload?: PageByIdResponse }) => {
        state.page_to_update = action.payload as PageByIdResponse; // legacy plain action: always dispatched with res.data
      });
  },
});

export const {
  selectedFilters,
  clearPage,
  pageAdded,
  requiredPageAdded,
  pageDeleted,
  pageDeleteBlockersCleared,
} = pagesSlice.actions;

export { initialState };
export default pagesSlice.reducer;
